/**
 * Recurrence rules for Scheduled Sends.
 *
 * Same shape as `milestones.ts`'s `plan()`: pure, no I/O, the date is always a
 * parameter rather than read from the clock in here. That is what makes this
 * testable at all, and it is what let the milestone engine be verified
 * against real data before it ever touched a database.
 *
 * Dates in and out of this file are local calendar dates as `YYYY-MM-DD`
 * strings — the same shape `localDate()` in `campaigns.ts` produces, and for
 * the same reason: `Date`'s own ISO string is UTC, and this system has
 * already paid for that mistake three times (the licence filter, every
 * date-only column since Phase A, the tab counts at midnight). Callers are
 * responsible for turning "now, in America/New_York" into that string before
 * calling in — this file does not know what "now" is, on purpose.
 */

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6 // 0 = Sunday, matches campaigns.meeting_day_of_week and Date#getDay()

export type CadenceRule =
  | { type: 'weekly'; day_of_week: Weekday }
  | {
      /**
       * `anchor_date` MUST itself fall on `day_of_week` — it is what the N-week
       * cycle counts from, not just an arbitrary reference point. `validateCadence`
       * rejects an anchor that does not match its own day_of_week, because a
       * mismatched anchor does not fail loudly on its own: it just fires on the
       * wrong weeks forever, silently, and nobody would notice until Renee asks
       * why the reminder skipped a week it should have hit.
       */
      type: 'every_n_weeks'
      day_of_week: Weekday
      n: number // >= 1; n=1 is equivalent to 'weekly' but callers should prefer 'weekly' for that case
      anchor_date: string
    }
  | {
      /**
       * A day_of_month past the end of a shorter month clamps to that month's
       * last day — "the 31st" still fires on 28/29 Feb rather than never firing
       * that month at all. Documented behaviour, not an accident: the
       * alternative (skip the month entirely) is a silent miss of exactly the
       * kind this project keeps closing elsewhere.
       */
      type: 'monthly_date'
      day_of_month: number // 1-31
    }
  | {
      /**
       * One rule can hold more than one week — "1st and 3rd Tuesday" is a
       * single CadenceRule, not two campaigns running in parallel. `-1` means
       * the LAST occurrence of that weekday in the month (handles both
       * 4-and-5-occurrence months without the caller needing to know which).
       */
      type: 'monthly_nth_weekday'
      day_of_week: Weekday
      weeks: number[] // each 1-4 or -1
    }

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** `YYYY-MM-DD` -> a Date at local midnight, so field reads (getDay, getDate...) are unambiguous. */
function parseLocalDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function daysInMonth(year: number, month0: number): number {
  return new Date(year, month0 + 1, 0).getDate()
}

/** Every date this weekday falls on within the given month, in order — 4 or 5 of them. */
function weekdayDatesInMonth(year: number, month0: number, weekday: Weekday): number[] {
  const out: number[] = []
  const total = daysInMonth(year, month0)
  for (let day = 1; day <= total; day++) {
    if (new Date(year, month0, day).getDay() === weekday) out.push(day)
  }
  return out
}

export type CadenceError = string

/**
 * Checked separately from `firesOn` so a bad rule can be rejected at save
 * time, on the screen where Renee is looking, rather than failing silently
 * every morning after.
 */
export function validateCadence(rule: CadenceRule): CadenceError | null {
  switch (rule.type) {
    case 'weekly':
      if (rule.day_of_week < 0 || rule.day_of_week > 6) return 'day_of_week must be 0-6'
      return null
    case 'every_n_weeks': {
      if (rule.day_of_week < 0 || rule.day_of_week > 6) return 'day_of_week must be 0-6'
      if (!Number.isInteger(rule.n) || rule.n < 1) return 'n must be a whole number of 1 or more'
      if (!DATE_RE.test(rule.anchor_date)) return 'anchor_date must be YYYY-MM-DD'
      const anchor = parseLocalDate(rule.anchor_date)
      if (Number.isNaN(anchor.getTime())) return 'anchor_date is not a real date'
      if (anchor.getDay() !== rule.day_of_week) {
        return 'anchor_date must itself fall on day_of_week, or every_n_weeks fires on the wrong weeks forever'
      }
      return null
    }
    case 'monthly_date':
      if (!Number.isInteger(rule.day_of_month) || rule.day_of_month < 1 || rule.day_of_month > 31) {
        return 'day_of_month must be 1-31'
      }
      return null
    case 'monthly_nth_weekday': {
      if (rule.day_of_week < 0 || rule.day_of_week > 6) return 'day_of_week must be 0-6'
      if (!Array.isArray(rule.weeks) || rule.weeks.length === 0) return 'weeks must hold at least one value'
      for (const w of rule.weeks) {
        if (!Number.isInteger(w) || w === 0 || w < -1 || w > 4) {
          return 'each week must be 1, 2, 3, 4, or -1 (last)'
        }
      }
      return null
    }
  }
}

/**
 * Does this rule fire on this date?
 *
 * `dateStr` is a local calendar date, `YYYY-MM-DD` — see the file header.
 * Does not validate the rule; callers validate once at save time
 * (`validateCadence`) and trust it after that, the same division of labour
 * `plan()` uses between "is this data usable" and "what should happen today".
 */
export function firesOn(rule: CadenceRule, dateStr: string): boolean {
  const date = parseLocalDate(dateStr)
  if (Number.isNaN(date.getTime())) return false

  switch (rule.type) {
    case 'weekly':
      return date.getDay() === rule.day_of_week

    case 'every_n_weeks': {
      if (date.getDay() !== rule.day_of_week) return false
      const anchor = parseLocalDate(rule.anchor_date)
      // Whole weeks between anchor and date. Both are local midnight on the
      // SAME weekday (enforced by validateCadence), so this division is exact
      // — no rounding, no off-by-one from a partial week at either end.
      const msPerWeek = 7 * 24 * 60 * 60 * 1000
      const weeksBetween = Math.round((date.getTime() - anchor.getTime()) / msPerWeek)
      // A negative remainder (date before anchor) is a real case — a campaign
      // created today with an anchor that predates it — and must still land on
      // a clean cycle boundary, not just decline every date before the anchor.
      return ((weeksBetween % rule.n) + rule.n) % rule.n === 0
    }

    case 'monthly_date': {
      const y = date.getFullYear()
      const m0 = date.getMonth()
      const lastDay = daysInMonth(y, m0)
      const target = Math.min(rule.day_of_month, lastDay) // clamp — see the type's own comment
      return date.getDate() === target
    }

    case 'monthly_nth_weekday': {
      if (date.getDay() !== rule.day_of_week) return false
      const y = date.getFullYear()
      const m0 = date.getMonth()
      const occurrences = weekdayDatesInMonth(y, m0, rule.day_of_week)
      const day = date.getDate()
      return rule.weeks.some((w) => {
        const idx = w === -1 ? occurrences.length - 1 : w - 1
        return occurrences[idx] === day
      })
    }
  }
}

/**
 * The next date on or after `fromDateStr` (inclusive) that the rule fires —
 * used for previewing a campaign before it's saved ("next send: Mon, Aug 25")
 * and nowhere in the generation path itself, which only ever asks `firesOn`
 * about today. Bounded search rather than a closed-form solution: cadence
 * rules are irregular enough (nth-weekday, clamped month-end) that a loop
 * over real calendar dates is more obviously correct than an arithmetic
 * shortcut, and a year is far more headroom than any real rule needs.
 */
export function nextOccurrenceOnOrAfter(rule: CadenceRule, fromDateStr: string): string | null {
  const start = parseLocalDate(fromDateStr)
  if (Number.isNaN(start.getTime())) return null
  for (let i = 0; i < 366; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
    const s = localDateOf(d)
    if (firesOn(rule, s)) return s
  }
  return null
}

function localDateOf(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
